import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@wundo/ui';
import type {
  MeetingCorrectionScope,
  MeetingKind,
  MeetingThreadItem,
} from '@wundo/shared/meetingPage.ts';
import { meetingAlwaysLabel } from './meetingAlwaysLabel.ts';
import { m } from '@/paraglide/messages.js';

export interface MeetingRemoveDialogProps {
  open: boolean;
  /** The thread the "always" answer would land on, when there already is one. */
  thread: MeetingThreadItem | null;
  /** What Wundo takes this meeting to be, for a meeting with no thread yet. */
  kind: MeetingKind;
  /**
   * Whether this is an event Wundo never picks up on its own. Only a choice for
   * this one sitting reaches it, so there is no question left to ask.
   */
  occurrenceOnly: boolean;
  failed: boolean;
  pending: boolean;
  onChoose: (scope: MeetingCorrectionScope) => void;
  onCancel: () => void;
}

/**
 * Taking Wundo off a conversation, from the conversation's own page.
 *
 * It says what removal costs before it asks how far it reaches, because the
 * page behind the dialog is full of preparation and verslag and the fear is
 * that the click empties it. It does not: what stops is the help ahead.
 *
 * The scope question is the same one every other correction asks, and it is
 * asked the same way — the far answer named after what it covers rather than
 * called "always". On an event Wundo never picks up by itself there is nothing
 * to ask, so the dialog states the act instead of offering two answers to it.
 */
export function MeetingRemoveDialog({
  open,
  thread,
  kind,
  occurrenceOnly,
  failed,
  pending,
  onChoose,
  onCancel,
}: MeetingRemoveDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>{m.meeting_remove_title()}</DialogTitle>
        </DialogHeader>

        <div className='flex flex-col gap-2.5 px-5 py-4'>
          <p className='text-sm text-ink-body'>{m.meeting_remove_body()}</p>

          {occurrenceOnly ? (
            <Button
              shape='block'
              fullWidth
              disabled={pending}
              onClick={() => onChoose('occurrence')}
            >
              {m.meeting_remove_wundo()}
            </Button>
          ) : (
            <>
              <Button
                shape='block'
                variant='quiet'
                fullWidth
                disabled={pending}
                onClick={() => onChoose('occurrence')}
              >
                {m.meeting_scope_once()}
              </Button>
              <Button
                shape='block'
                fullWidth
                disabled={pending}
                onClick={() => onChoose('always')}
              >
                {meetingAlwaysLabel(thread, kind)}
              </Button>
            </>
          )}

          {failed && (
            <p role='alert' className='text-sm text-danger'>
              {m.meeting_action_failed()}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button size='sm' variant='quiet' onClick={onCancel}>
            {m.meeting_cancel()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
